import { Injectable, inject } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { FormFieldMapper, LoggerProvider, FormField } from '@shared';
import { GetCaseFormVersionByIdUseCase } from '@features/case-forms';
import { GetCaseFieldValuesUseCase } from '../get-case-field-values';
import { CasesState } from '../../states/cases.state';
import { GetCaseFormFieldsCommand } from './get-case-form-fields.command';

/**
 * Get Case Form Fields Use Case (Edit Mode)
 * Fetches field definitions first, then field values, and merges them
 * Returns FormField[] ready for CaseFormWidget
 *
 * IMPORTANT: This is optimized for EDIT MODE where we need full form structure
 */
@Injectable({ providedIn: 'root' })
export class GetCaseFormFieldsUseCase {
  private readonly _getCaseFormVersionUseCase = inject(GetCaseFormVersionByIdUseCase);
  private readonly _getCaseFieldValuesUseCase = inject(GetCaseFieldValuesUseCase);
  private readonly _formFieldMapper = inject(FormFieldMapper);
  private readonly _state = inject(CasesState);
  private readonly _logger = inject(LoggerProvider).createScoped('GetCaseFormFieldsUseCase');

  /**
   * Executes the use case
   * @param command - Command with caseFormVersionId and caseId
   * @returns Observable<FormField[]> - Form fields with values merged
   */
  public execute(command: GetCaseFormFieldsCommand): Observable<FormField[]> {
    const { caseFormVersionId, caseId, forceRefresh = false } = command;

    // STEP 1: Fetch form version (field definitions) first
    return this._getCaseFormVersionUseCase.execute({
      versionId: caseFormVersionId
    }).pipe(
      switchMap((caseFormDetail) => {
        // Validate caseFormDetail has fields
        if (!caseFormDetail?.fields) {
          throw new Error('Invalid case form version: missing fields');
        }

        // STEP 2: Map to FormField[] (without values yet)
        const formFields = this._formFieldMapper.mapToFormFields(caseFormDetail.fields);

        // STEP 3: Check if field values are already in state (cached from readonly mode)
        const caseEntity = this._state.selectedCase();
        const hasFieldValues = caseEntity?.fieldValues && caseEntity.fieldValues.length > 0;

        if (hasFieldValues && !forceRefresh) {
          // Use cached field values - no API call needed
          const fieldValuesMap = new Map(
            caseEntity.fieldValues.map(fv => [fv.versionFieldId, fv.value])
          );

          const mergedFields = formFields.map(field => ({
            ...field,
            defaultValue: this._parseFieldValue(
              fieldValuesMap.get(field.id) || null,
              field.type
            )
          })) as FormField[];

          return of(mergedFields);
        }

        // STEP 4: Fetch field values from API (not in cache or force refresh)
        return this._getCaseFieldValuesUseCase.execute({ caseId }).pipe(
          map((fieldValuesResult) => {
            // Merge field values with field definitions
            const fieldValuesMap = new Map(
              fieldValuesResult.fieldValues.map(fv => [fv.versionFieldId, fv.value])
            );

            const mergedFields = formFields.map(field => ({
              ...field,
              defaultValue: this._parseFieldValue(
                fieldValuesMap.get(field.id) || null,
                field.type
              )
            })) as FormField[];

            return mergedFields;
          })
        );
      }),
      catchError((error) => {
        const errorMessage = error.message || 'Failed to load case form fields';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Parses stored field value based on field type
   */
  private _parseFieldValue(value: string | null, fieldType: string): string | number | boolean | Date | string[] | null {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);

      // Date fields - return Date object for cm-date-field
      if (fieldType === 'date' && typeof parsed === 'string') {
        const dateValue = new Date(parsed);
        // Validate the date is valid
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }

      // Checkbox fields - return array for multi-select
      if (fieldType === 'checkbox' && Array.isArray(parsed)) {
        return parsed.map(String); // Ensure all values are strings
      }

      // Ensure we return only allowed types
      if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
        return parsed;
      }

      // For other arrays or objects, stringify back
      return JSON.stringify(parsed);
    } catch {
      // Return as-is if not valid JSON
      return value;
    }
  }
}
